import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Star, ShieldCheck, ArrowRight, ArrowUpRight, Search as SearchIcon,
  MapPin, Clock, Ticket as TicketIcon, Sparkles,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BookButton } from "@/components/common/BookButton";
import { SectionHeading, Overline } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { FeatureCard } from "@/components/cards/FeatureCard";
import { TicketCard } from "@/components/cards/TicketCard";
import { ReviewCard } from "@/components/cards/ReviewCard";
import { PreviewCard } from "@/components/cards/PreviewCard";
import { ComparisonTable } from "@/components/blocks/ComparisonTable";
import { FAQAccordion } from "@/components/blocks/FAQAccordion";
import { CTASection } from "@/components/common/CTASection";
import { IMG } from "@/data/images";
import { getTicket } from "@/data/tickets";
import { HIGHLIGHTS } from "@/data/highlights";
import { PLANNING } from "@/data/planning";
import { STATS, TRUST_POINTS, REVIEWS, TOP_FAQS } from "@/data/site";

const FINDER_OPTIONS = [
  { value: "skip-the-line", label: "First visit — keep it simple" },
  { value: "guided-tours", label: "I want a guided tour" },
  { value: "underground", label: "Underground & arena floor" },
  { value: "night-tours", label: "An evening / night experience" },
  { value: "family", label: "Travelling with family" },
  { value: "entry", label: "On a budget" },
  { value: "combo", label: "Colosseum + another sight" },
  { value: "last-minute", label: "Last minute / today" },
];

/* ---------------- Hero ---------------- */
const Hero = () => {
  const navigate = useNavigate();
  const [choice, setChoice] = useState("");

  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0">
        <img src={IMG.heroSunset} alt="The Colosseum at golden hour" className="h-full w-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/55 to-charcoal/30" />
      </div>

      <div className="container-wide relative flex min-h-[88vh] flex-col justify-end pb-14 pt-28 md:min-h-[92vh] md:pb-20">
        <Reveal className="max-w-3xl">
          <Overline className="text-gold-light">romancolosseumtickets.com</Overline>
          <h1 className="mt-5 font-serif text-5xl font-medium leading-[1.02] tracking-tight text-white md:text-6xl lg:text-7xl text-balance">
            Step into 2,000 years of history
          </h1>
          <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-white/80 md:text-xl">
            Compare every Colosseum ticket, skip the queues and book with confidence — your independent guide to visiting Rome's greatest monument.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <BookButton label="Book Your Tickets" size="lg" testId="hero-book-now" />
            <Link
              to="/tickets"
              data-testid="hero-compare-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-8 py-4 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/15"
            >
              Compare ticket types <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-white/75">
            <span className="flex items-center gap-2">
              <Star size={16} className="fill-gold text-gold" /> 4.8/5 from 120,000+ travellers
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-gold-light" /> Free cancellation available
            </span>
          </div>
        </Reveal>

        {/* Ticket finder */}
        <Reveal delay={0.15} className="mt-10 w-full max-w-3xl">
          <div
            data-testid="ticket-finder"
            className="rounded-2xl border border-white/15 bg-travertine/95 p-5 shadow-lift backdrop-blur-xl md:p-6"
          >
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gold-dark">
              <Sparkles size={16} /> Find your perfect ticket
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Select value={choice} onValueChange={setChoice}>
                <SelectTrigger
                  className="h-14 flex-1 rounded-xl border-stone-line bg-white text-base text-charcoal"
                  data-testid="finder-select"
                >
                  <SelectValue placeholder="What kind of visit are you planning?" />
                </SelectTrigger>
                <SelectContent>
                  {FINDER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={() => navigate(choice ? `/tickets/${choice}` : "/tickets")}
                data-testid="finder-submit"
                className="flex h-14 items-center justify-center gap-2 rounded-xl bg-charcoal px-7 font-medium text-white transition-colors hover:bg-black"
              >
                <SearchIcon size={17} /> Find tickets
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Stats ---------------- */
const StatsStrip = () => (
  <section className="border-b border-stone-line bg-white" data-testid="home-stats">
    <div className="container-wide grid grid-cols-2 gap-8 py-12 md:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={i} delay={i * 0.08} className="text-center">
          <div className="font-serif text-4xl font-medium text-gold-dark md:text-5xl">{s.value}</div>
          <div className="mt-1.5 text-xs uppercase tracking-wider text-slatey md:text-sm">{s.label}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Featured tickets ---------------- */
const FeaturedTickets = () => {
  const featured = ["skip-the-line", "guided-tours", "underground"].map(getTicket);
  return (
    <section className="container-wide py-20 md:py-28" data-testid="home-featured-tickets">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading
          overline="Featured tickets"
          title="The most-loved ways to visit"
          description="Hand-picked experiences that travellers book again and again. Every option includes the Roman Forum and Palatine Hill."
        />
        <Link
          to="/tickets"
          data-testid="featured-view-all"
          className="inline-flex items-center gap-1.5 rounded-full border border-charcoal px-6 py-3 text-sm font-medium text-charcoal transition-colors hover:bg-charcoal hover:text-white"
        >
          View all 9 tickets <ArrowRight size={15} />
        </Link>
      </div>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {featured.map((t, i) => (
          <Reveal key={t.slug} delay={i * 0.1}>
            <TicketCard ticket={t} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Why book with us ---------------- */
const WhyBook = () => (
  <section className="bg-sand/50 border-y border-stone-line" data-testid="home-why-book">
    <div className="container-wide py-20 md:py-28">
      <SectionHeading
        align="center"
        overline="Why book with us"
        title="Plan with total confidence"
        description="We're an independent guide dedicated entirely to the Colosseum — here to make choosing and booking effortless."
        className="mx-auto"
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {TRUST_POINTS.map((p, i) => (
          <Reveal key={i} delay={(i % 4) * 0.08}>
            <FeatureCard icon={p.icon} title={p.title} text={p.text} className="h-full" />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Comparison preview ---------------- */
const ComparisonPreview = () => {
  const rows = ["entry", "skip-the-line", "guided-tours", "underground", "family"]
    .map(getTicket)
    .map((t) => [t.shortName || t.name, `€${t.price}`, t.duration, t.badge || "—"]);
  return (
    <section className="container-wide py-20 md:py-28" data-testid="home-comparison">
      <SectionHeading
        overline="Compare at a glance"
        title="Which ticket is right for you?"
        description="A quick side-by-side of popular options. Open any ticket for the full breakdown of what's included."
      />
      <Reveal className="mt-10">
        <ComparisonTable
          columns={["Ticket", "From", "Duration", "Best known for"]}
          rows={rows}
        />
      </Reveal>
      <Reveal className="mt-6">
        <Link
          to="/tickets"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gold-dark hover:gap-2.5 transition-all"
        >
          See the full comparison <ArrowRight size={15} />
        </Link>
      </Reveal>
    </section>
  );
};

/* ---------------- Highlights bento ---------------- */
const HighlightsBento = () => {
  const spans = [
    "lg:col-span-2 lg:row-span-2",
    "lg:col-span-2",
    "lg:col-span-1",
    "lg:col-span-1",
    "lg:col-span-2",
  ];
  return (
    <section className="bg-charcoal" data-testid="home-highlights">
      <div className="container-wide py-20 md:py-28">
        <SectionHeading
          overline="Inside the Colosseum"
          title="Highlights worth seeking out"
          description="From the hidden underground to the imperial heights of Palatine Hill — discover what makes each part unforgettable."
          titleClassName="text-white"
          className="[&_p]:text-white/70"
        />
        <div className="mt-12 grid auto-rows-[200px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.slug} delay={(i % 3) * 0.08} className={spans[i] || ""}>
              <Link
                to={`/highlights/${h.slug}`}
                data-testid={`home-highlight-${h.slug}`}
                className="group relative block h-full overflow-hidden rounded-2xl"
              >
                <img
                  src={h.heroImage}
                  alt={h.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-serif text-2xl font-medium text-white">{h.title}</h3>
                  <span className="mt-1 inline-flex items-center gap-1 text-sm text-gold-light opacity-0 transition-opacity group-hover:opacity-100">
                    Explore <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Plan overview ---------------- */
const PlanOverview = () => {
  const pages = ["opening-hours", "best-time-to-visit", "how-to-reach", "tips"]
    .map((s) => PLANNING.find((p) => p.slug === s));
  return (
    <section className="container-wide py-20 md:py-28" data-testid="home-plan">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading
          overline="Plan your visit"
          title="Everything sorted before you arrive"
          description="Practical, no-nonsense guides so nothing catches you out on the day."
        />
        <Link
          to="/plan"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gold-dark hover:gap-2.5 transition-all"
        >
          All planning guides <ArrowRight size={15} />
        </Link>
      </div>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {pages.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 4) * 0.07}>
            <PreviewCard to={`/plan/${p.slug}`} image={p.heroImage} title={p.title} text={p.subtitle} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Map preview ---------------- */
const MapPreview = () => (
  <section className="bg-sand/50 border-y border-stone-line" data-testid="home-map">
    <div className="container-wide grid gap-10 py-20 md:grid-cols-2 md:items-center md:py-28">
      <Reveal>
        <SectionHeading
          overline="Find your way"
          title="Right in the heart of ancient Rome"
          description="The Colosseum, Roman Forum and Palatine Hill form a compact, walkable triangle — and the metro drops you at the door."
        />
        <ul className="mt-8 space-y-4">
          {[
            { icon: MapPin, text: "Metro Line B to 'Colosseo' — the exit faces the monument." },
            { icon: Clock, text: "Open from 9:00 daily, with seasonal closing times." },
            { icon: TicketIcon, text: "One ticket covers the Colosseum, Forum and Palatine Hill." },
          ].map((row, i) => (
            <li key={i} className="flex items-center gap-3 text-[15px] text-charcoal">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-gold-dark">
                <row.icon size={18} />
              </span>
              {row.text}
            </li>
          ))}
        </ul>
        <Link
          to="/plan/map"
          className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-charcoal px-6 py-3 text-sm font-medium text-charcoal transition-colors hover:bg-charcoal hover:text-white"
        >
          Open the interactive map <ArrowRight size={15} />
        </Link>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="overflow-hidden rounded-2xl border border-stone-line shadow-soft">
          <iframe
            title="Colosseum location"
            src="https://www.google.com/maps?q=Colosseum,Rome&output=embed"
            className="h-[360px] w-full md:h-[440px]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Reviews ---------------- */
const Reviews = () => (
  <section className="container-wide py-20 md:py-28" data-testid="home-reviews">
    <SectionHeading
      align="center"
      className="mx-auto"
      overline="Loved by travellers"
      title="Real visits, real reviews"
      description="Join hundreds of thousands of visitors who planned their Colosseum trip the easy way."
    />
    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {REVIEWS.map((r, i) => (
        <Reveal key={i} delay={(i % 4) * 0.08}>
          <ReviewCard review={r} />
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- FAQ preview ---------------- */
const FAQPreview = () => (
  <section className="bg-sand/50 border-y border-stone-line" data-testid="home-faq">
    <div className="container-tight py-20 md:py-28">
      <SectionHeading
        align="center"
        className="mx-auto"
        overline="Before you book"
        title="Frequently asked questions"
      />
      <div className="mx-auto mt-10 max-w-3xl">
        <FAQAccordion items={TOP_FAQS} testId="home-faq-accordion" />
        <div className="mt-8 text-center">
          <Link
            to="/plan/faqs"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gold-dark hover:gap-2.5 transition-all"
          >
            See all planning FAQs <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <StatsStrip />
      <FeaturedTickets />
      <WhyBook />
      <ComparisonPreview />
      <HighlightsBento />
      <PlanOverview />
      <MapPreview />
      <Reviews />
      <FAQPreview />
      <div className="container-wide py-20 md:py-24">
        <CTASection />
      </div>
    </>
  );
}
